import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Fingerprint, Network, Cable, BatteryCharging, PhoneCall } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { CTABand } from "../../components/site/CTABand";
import { Reveal, Stagger, StaggerItem } from "../../components/motion/Reveal";

const BRAND_CATEGORIES = [
  {
    slug: "security-surveillance", name: "Security & Surveillance", icon: ShieldCheck,
    brands: [
      { name: "Hikvision", logo: "/brands/hikvision.png" },
      { name: "Dahua", logo: "/brands/dahua.svg" },
      { name: "CP Plus", logo: null },
      { name: "Axis", logo: null },
      { name: "Bosch", logo: "/brands/bosch.svg" },
      { name: "Honeywell", logo: "/brands/honeywell.svg" },
      { name: "Pelco", logo: null },
    ],
  },
  {
    slug: "access-biometrics", name: "Access Control & Biometrics", icon: Fingerprint,
    brands: [
      { name: "ZKTeco", logo: null },
      { name: "eSSL", logo: "/brands/essl.png" },
      { name: "Matrix", logo: null },
    ],
  },
  {
    slug: "networking-infrastructure", name: "Networking & Infrastructure", icon: Network,
    brands: [
      { name: "Cisco", logo: "/brands/cisco.svg" },
      { name: "Palo Alto Networks", logo: null },
      { name: "TP-Link Omada", logo: "/brands/tplink.svg" },
      { name: "Ubiquiti", logo: "/brands/ubiquiti.svg" },
      { name: "HPE Aruba", logo: null },
      { name: "D-Link", logo: "/brands/dlink.png" },
      { name: "Netgear", logo: "/brands/netgear.svg" },
      { name: "Juniper Networks", logo: null },
      { name: "Ruckus", logo: null },
      { name: "Fortinet", logo: "/brands/fortinet.svg" },
    ],
  },
  {
    slug: "structured-cabling", name: "Structured Cabling", icon: Cable,
    brands: [
      { name: "Legrand", logo: null },
      { name: "Panduit", logo: "/brands/panduit.svg" },
      { name: "Belden", logo: null },
      { name: "Molex", logo: "/brands/molex.png" },
      { name: "CommScope", logo: null },
      { name: "Finolex", logo: null },
      { name: "Polycab", logo: null },
    ],
  },
  {
    slug: "ups-power", name: "UPS & Power", icon: BatteryCharging,
    brands: [
      { name: "APC by Schneider Electric", logo: "/brands/apc.png" },
      { name: "Luminous", logo: null },
      { name: "Microtek", logo: null },
      { name: "Exide", logo: null },
    ],
  },
  {
    slug: "communication", name: "Communication Systems", icon: PhoneCall,
    brands: [
      { name: "Panasonic", logo: "/brands/panasonic.svg" },
      { name: "NEC", logo: null },
      { name: "Alcatel-Lucent", logo: null },
    ],
  },
];

export default function Brands() {
  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="brands-page">
      <SiteHeader />
      <PageHero
        crumb="Brands We Deal With"
        eyebrow="Brands We Deal With"
        title="Trusted Brands,"
        titleAccent="Proven Technology."
        description="We deal with the world's leading security and technology brands to deliver reliable, enterprise-grade solutions."
      />
      <section className="py-16 sm:py-24">
        <div className="nx-wrap">
          {BRAND_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <div key={cat.slug} className="mb-14 last:mb-0" data-testid={`brand-category-${cat.slug}`}>
                <Reveal>
                  <div className="flex items-center gap-3.5 mb-7">
                    <Icon size={20} className="text-nx-goldlight" strokeWidth={1.5} />
                    <h2 className="font-marcellus text-xl sm:text-2xl text-white">{cat.name}</h2>
                    <span className="h-px flex-1 bg-white/10" />
                  </div>
                </Reveal>
                <Stagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-px bg-white/10 border border-white/10">
                  {cat.brands.map((b) => (
                    <StaggerItem key={b.name}>
                      <div className="flex items-center justify-center h-20 bg-nx-navy2 hover:bg-nx-navy transition-colors px-4" data-testid={`brand-card-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {b.logo ? (
                          <img src={b.logo} alt={b.name} className="max-h-8 max-w-[120px] w-auto object-contain brightness-0 invert opacity-80 hover:opacity-100 transition-opacity" loading="lazy" />
                        ) : (
                          <span className="font-marcellus text-sm sm:text-base text-slate-300 tracking-wide text-center">{b.name}</span>
                        )}
                      </div>
                    </StaggerItem>
                  ))}
                </Stagger>
              </div>
            );
          })}
          <Reveal delay={0.1} className="mt-14">
            <p className="text-xs text-slate-600 leading-relaxed max-w-2xl mb-8" data-testid="brands-placeholder-note">Some brand marks show as text placeholders — official logos are added as manufacturer brand guidelines and permissions are confirmed.</p>
            <Link to="/inquiries" className="group inline-flex items-center gap-3 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-sm tracking-wide px-9 py-4 transition-all duration-300 hover:-translate-y-0.5" data-testid="brands-cta-inquiries">
              Ask About a Brand <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
      <CTABand />
      <SiteFooter />
    </div>
  );
}
